import React from 'react';
import { useNavigate } from 'react-router-dom';
import AdminBreadcrumb from '../AdminBreadcrumb';
import AdminButton from './AdminButton';

/**
 * AdminPageHeader
 * 
 * Standardized page header component for admin pages.
 * 
 * @param {string} title - Page title (required)
 * @param {string} subtitle - Optional subtitle/description
 * @param {ReactNode} rightActions - Optional actions to display on the right (alias: actions)
 * @param {Array} breadcrumbItems - Optional custom breadcrumb items
 * @param {boolean} showBackToHome - Show "Back to Admin Home" button (default: true)
 * @param {boolean} showBreadcrumb - Show breadcrumb row (default: true). Set false when layout renders breadcrumbs.
 */
export default function AdminPageHeader({ 
  title, 
  subtitle, 
  rightActions: rightActionsProp,
  actions,
  breadcrumbItems,
  showBackToHome = true,
  showBreadcrumb = true,
}) {
  const navigate = useNavigate();
  const rightActions = rightActionsProp ?? actions;

  const showTop = showBackToHome || showBreadcrumb;
  return (
    <div className="admin-page-header">
      {showTop && (
        <div style={styles.top}>
          {showBackToHome && (
            <AdminButton
              variant="ghost"
              onClick={() => navigate('/admin')}
              ariaLabel="Back to Admin Home"
              style={styles.backButton}
            >
              ← Back to Admin Home
            </AdminButton>
          )}
          {showBreadcrumb && <AdminBreadcrumb items={breadcrumbItems} />}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--spacing-4)' }}>
        <div className="admin-page-header-left">
          <h1 className="admin-page-header-title">{title}</h1>
          {subtitle && <p className="admin-page-header-subtitle">{subtitle}</p>}
        </div>
        {rightActions && (
          <div className="admin-page-header-right">
            {rightActions}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  top: {
    marginBottom: 'var(--spacing-2)', // 16px
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)', // 16px
    flexWrap: 'wrap',
  },
  backButton: {
    fontSize: 'var(--font-size-sm)', // 14px
    padding: 'var(--spacing-1) var(--spacing-2)', // 8px 16px
  },
};

